import {
  Activity,
  AlertCircle,
  ArrowDownUp,
  Award,
  Banknote,
  BarChart3,
  Boxes,
  Building2,
  CalendarDays,
  CalendarRange,
  ClipboardCheck,
  Clock,
  Contact,
  CreditCard,
  Database,
  Download,
  FileClock,
  FileText,
  Flame,
  Gauge,
  Gift,
  HandCoins,
  Grid2x2,
  Handshake,
  Home,
  Image,
  Inbox,
  Layers,
  LayoutDashboard,
  LifeBuoy,
  Link2,
  ListChecks,
  type LucideIcon,
  Mail,
  MapPin,
  Megaphone,
  MessageSquare,
  Monitor,
  PackageSearch,
  Percent,
  PiggyBank,
  Receipt,
  Repeat,
  ScrollText,
  Search,
  Settings,
  Shapes,
  ShieldBan,
  ShoppingBag,
  ShoppingCart,
  SlidersHorizontal,
  Sparkles,
  Store,
  Tag,
  Ticket,
  TrendingDown,
  TrendingUp,
  Truck,
  UserCog,
  UserPlus,
  Users,
  Wrench,
} from 'lucide-react';
import type { ClientFlag } from '@/config/client-flags';
import type { Feature } from '@/config/features';
import type { Permission } from '@/features/auth/roles';

/**
 * The navigation tree, as data.
 *
 * A port of the original `Sidebar.js` — its links, its order, its role gates —
 * replacing the placeholder tree that preceded it. Departures from the original
 * are marked inline: the Booking section is regrouped at the client's request,
 * a few groups are renamed, and entries whose labels collided in the original
 * are disambiguated.
 *
 * Visibility is declared, never coded:
 *   permission        — a named capability from features/auth/roles
 *   feature           — the client bought the module (derived from env)
 *   flag              — a per-client / per-store switch (config/client-flags)
 *   requiresBusiness  — a specific store is selected
 *   globalOnly        — no store is selected
 */

export type NavAction = 'changeApiUrl' | 'newBusiness';

/**
 * Where an entry sits in the agreed build order. Everything except `built` is
 * hidden from the sidebar and the command palette — see `status` below.
 *
 *   1  Customer Service: Users & Orders
 *   2  Booking (all)
 *   3  Catalog (all)
 *   4  Store: Staff, POS, Settings
 *   5  Gift Cards, Marketing, Admin Services, Inventory, the finance reports
 *   later  Undecided — kept in the tree so nothing is lost, shown to nobody.
 */
export type NavPhase = 1 | 2 | 3 | 4 | 5 | 'later';

/**
 * `built` is the only status that renders normally. Everything else is emitted
 * with `data-nav-status="planned"` and the `nav-planned` class, and dropped by
 * one CSS rule in styles/index.css — so an unbuilt page is one word away from
 * appearing, and the whole roadmap can be revealed at once for review with
 * `document.documentElement.classList.add('show-planned')`.
 *
 * Kept as data rather than commented-out blocks: commenting loses the role
 * gates, the icons and the ordering that this port exists to preserve.
 */
export type NavStatus = 'built' | 'planned';

export interface NavItem {
  /** Stable id — active state, persisted open groups, search. Unique tree-wide. */
  key: string;
  /** i18n key, resolved at render — never a literal. */
  label: string;
  icon?: LucideIcon;
  /** Omitted on a group and on an action. */
  to?: string;
  /** Fires a handler instead of navigating (the original had two of these). */
  action?: NavAction;
  permission?: Permission;
  feature?: Feature;
  flag?: ClientFlag;
  requiresBusiness?: boolean;
  globalOnly?: boolean;
  /** Rendered but not selectable — the original marked one entry this way. */
  disabled?: boolean;
  /**
   * Kept out of the sidebar, but still a real entry everywhere else.
   *
   * For a route that is a *tab* of another page: it needs no row of its own,
   * yet it still has to resolve for the header's breadcrumb and be findable in
   * the command palette. Dropping the entry entirely lost both.
   */
  hideInSidebar?: boolean;
  /** Small tag after the label, e.g. Beta. */
  tag?: string;
  badge?: string | number;
  /** Extra terms the command palette should match on. */
  keywords?: string[];
  /** Defaults to 'planned' — only an explicit 'built' shows in the sidebar. */
  status?: NavStatus;
  /** Position in the agreed build order; inherited by children. */
  phase?: NavPhase;
  children?: NavItem[];
}

export interface NavSection {
  key: string;
  /** i18n key for the small uppercase heading; omit for an unlabelled block. */
  label?: string;
  items: NavItem[];
}

export const navigation: NavSection[] = [
  // ---------------------------------------------------------------- overview
  {
    key: 'overview',
    items: [
      {
        key: 'home',
        label: 'nav.home',
        icon: Home,
        to: '/',
        keywords: ['welcome', 'start', 'shortcuts'],
        status: 'built',
      },
      {
        key: 'global-dashboard',
        label: 'nav.globalDashboard',
        icon: LayoutDashboard,
        to: '/dashboard',
        permission: 'viewGlobalDashboard',
        keywords: ['home', 'kpi', 'overview'],
        status: 'built',
      },
      {
        key: 'store-dashboard',
        label: 'nav.storeDashboard',
        icon: Gauge,
        to: '/storedashboard',
        permission: 'viewStoreDashboard',
        requiresBusiness: true,
        status: 'built',
      },
    ],
  },

  // ---------------------------------------------------------------- platform
  {
    key: 'platform',
    label: 'nav.sections.platform',
    items: [
      {
        key: 'admin-services',
        phase: 'later',
        label: 'nav.adminServices',
        icon: Wrench,
        permission: 'isSuperAdmin',
        children: [
          { key: 'business-accounts', label: 'nav.businessAccounts', icon: Building2, to: '/accounts', keywords: ['payouts', 'bank', 'stripe', 'merchant'], phase: 5, status: 'built' },
          { key: 'new-business', label: 'nav.newBusiness', icon: UserPlus, action: 'newBusiness', keywords: ['create store', 'onboard'], phase: 5, status: 'built' },
          { key: 'document-acceptance', label: 'nav.documentAcceptance', icon: ClipboardCheck, to: '/document-acceptance' },
          { key: 'documents-admin', label: 'nav.documents', icon: FileText, to: '/documents' },
          { key: 'impersonation', label: 'nav.impersonation', icon: UserCog, to: '/impersonation', keywords: ['sign in as', 'support', 'debug'], phase: 5, status: 'built' },
          { key: 'coupons-admin', label: 'nav.coupons', icon: Ticket, to: '/coupons' },
          { key: 'card-restrictions', label: 'nav.cardRestrictions', icon: ShieldBan, to: '/cardrestrictions' },
          { key: 'delivery-providers', label: 'nav.deliveryProviders', icon: Truck, to: '/deliveryproviders' },
          { key: 'admin-pos-lanes', label: 'nav.adminPosLanes', icon: Monitor, to: '/admin/pos', keywords: ['tills', 'registers', 'fleet', 'versions'], phase: 5, status: 'built' },
          { key: 'images-service', label: 'nav.imagesService', icon: Image, to: '/images', feature: 'images' },
          { key: 'logs-global', label: 'nav.logsGlobal', icon: ScrollText, to: '/logs' },
          { key: 'change-api-url', label: 'nav.changeApiUrl', icon: Link2, action: 'changeApiUrl' },
          // One entry, not three. The originals were the same component with a
          // different filename, and deciding which of the three to draw cost the
          // sidebar a blob-storage request per file on every render.
          { key: 'configuration', label: 'nav.configuration', icon: SlidersHorizontal, to: '/configuration', keywords: ['settings', 'server.json', 'app.json', 'manager.json'], phase: 5, status: 'built' },
        ],
      },
      {
        key: 'customer-service',
        phase: 'later',
        label: 'nav.customerService',
        icon: LifeBuoy,
        permission: 'isCustomerService',
        children: [
          { key: 'orders-global', label: 'nav.ordersGlobal', icon: Inbox, to: '/orders', keywords: ['purchases'], phase: 1, status: 'built' },
          { key: 'users-global', label: 'nav.users', icon: Users, to: '/customers', phase: 1, status: 'built' },
          // Only some clients invite the public to apply for an account, so the
          // whole screen is gated on `VITE_HAS_ACCOUNT_REQUESTS`.
          { key: 'account-requests', label: 'nav.accountRequests', icon: UserPlus, to: '/customers/requests', feature: 'accountRequests', keywords: ['signups', 'applications', 'approvals'], phase: 1, status: 'built' },
          { key: 'referral-email', label: 'nav.referralEmail', icon: Mail, to: '/referrals' },
          { key: 'external-contacts', label: 'nav.externalContacts', icon: Contact, to: '/contacts' },
          // NOTE: same route as Store > Layaway > Layaways in the original.
          // Two entries pointing at one page make both highlight at once; see
          // the open questions in the README before wiring the real page.
          { key: 'layaways-global', label: 'nav.layawaysGlobal', icon: PackageSearch, to: '/layaways' },
          { key: 'paypal-invoices-global', label: 'nav.paypalInvoicesGlobal', icon: Receipt, to: '/paypal/global/invoices' },
        ],
      },
      // NEW, not a port. Its own group rather than a row under Customer
      // Service or Marketing: the visits it reads include the staff's own use
      // of this portal, which is neither a support case nor a campaign, and
      // either of those gates would keep out half the people it is for.
      {
        key: 'analytics',
        label: 'nav.analytics',
        icon: BarChart3,
        permission: 'viewUsageAnalytics',
        children: [
          { key: 'usage-analytics', label: 'nav.usageAnalytics', icon: Activity, to: '/analytics/usage', keywords: ['page views', 'visits', 'trends', 'demographics', 'behaviour', 'segments'], status: 'built' },
        ],
      },
    ],
  },

  // ----------------------------------------------------------------- booking
  //
  // RESTRUCTURED at the client's request. The original was
  //   Booking > Maintenance | Reports | Settings | Heatmaps
  // where Reports also held two *management* grids — the `bookings` tab of
  // /booking/sales and the `memberships` tab of /memberships — so day-to-day
  // work sat four levels deep inside a report. Now:
  //   Booking > Maintenance | Management | Reports
  // Heatmaps folded into Reports, because they are reports. The old "Settings"
  // division folded into Maintenance: hosts, locations and service types are
  // the records an event or a plan is built out of, so creating and editing
  // them is the same job as creating and editing the events themselves.
  {
    key: 'booking',
    label: 'nav.sections.booking',
    items: [
      {
        key: 'booking-maintenance',
        phase: 2,
        label: 'nav.booking.maintenance',
        icon: Wrench,
        feature: 'booking',
        children: [
          { key: 'membership-plans', label: 'nav.booking.membershipPlans', icon: Award, to: '/booking/membership-plans', keywords: ['memberships', 'plans', 'subscriptions'], status: 'built' },
          { key: 'booking-events', label: 'nav.booking.events', icon: CalendarDays, to: '/booking/events', keywords: ['classes', 'timeslots'], status: 'built' },
          { key: 'membership-gifts', label: 'nav.booking.membershipGifts', icon: Gift, to: '/booking/membership-gifts', keywords: ['gifts'], status: 'built' },
          // The voucher catalogue. The API refuses these endpoints unless the
          // deployment has vouchers switched on, which today is MECA only; the
          // flag reads that switch from server.json.
          { key: 'gift-vouchers', label: 'nav.booking.giftVouchers', icon: Ticket, to: '/booking/gift-vouchers', keywords: ['vouchers', 'gift certificates', 'class packs', 'certificados'], flag: 'hasBookingVouchers', status: 'built' },
          // The supporting records an event or a plan is built out of. They
          // were their own "Setup" division, which split one job — creating and
          // editing the things the booking business is made of — across two
          // groups, and left a reader guessing which one held what.
          { key: 'booking-locations', label: 'nav.booking.locations', icon: MapPin, to: '/booking/locations', keywords: ['venues', 'check-in', 'qr'], status: 'built' },
          { key: 'booking-hosts', label: 'nav.booking.hosts', icon: Contact, to: '/booking/hosts', keywords: ['instructors', 'coaches'], status: 'built' },
          { key: 'booking-service-types', label: 'nav.booking.serviceTypes', icon: Shapes, to: '/booking/servicetypes', keywords: ['categories'], status: 'built' },
        ],
      },
      {
        key: 'booking-management',
        phase: 2,
        label: 'nav.booking.management',
        icon: ListChecks,
        feature: 'booking',
        children: [
          // Was the `bookings` tab of /booking/sales.
          { key: 'manage-bookings', label: 'nav.booking.manageBookings', icon: CalendarRange, to: '/booking/manage/bookings', keywords: ['reservations'], status: 'built' },
          // Was the `review` tab of /booking/sales — an action queue, not a
          // report. It is a tab of Bookings again, so it gets no sidebar row of
          // its own, but it keeps its breadcrumb and its palette entry.
          { key: 'payment-review', label: 'nav.booking.paymentReview', icon: HandCoins, to: '/booking/manage/payment-review', keywords: ['reconcile', 'paypal', 'unpaid'], status: 'built', hideInSidebar: true },
          // Was the `memberships` tab of /memberships.
          { key: 'manage-subscriptions', label: 'nav.booking.manageSubscriptions', icon: Repeat, to: '/booking/manage/subscriptions', keywords: ['enrolments'], status: 'built' },
          // Every voucher sold or issued. The catalogue it is sold from is under Maintenance.
          { key: 'manage-vouchers', label: 'nav.booking.giftVouchersManage', icon: Ticket, to: '/booking/manage/vouchers', keywords: ['vouchers', 'gift certificates', 'redeem code', 'certificados'], flag: 'hasBookingVouchers', status: 'built' },
        ],
      },
      {
        key: 'booking-reports',
        phase: 2,
        label: 'nav.booking.reports',
        icon: BarChart3,
        feature: 'booking',
        children: [
          { key: 'booking-overview', label: 'nav.booking.businessOverview', icon: Activity, to: '/booking/overview', keywords: ['dashboard', 'revenue', 'money'], status: 'built' },
          // Grouped so "Sales" reads unambiguously: on its own it could mean
          // anything the business sells, and the booking payment history that
          // belongs beside it had nowhere to sit.
          {
            key: 'booking-reports-bookings',
            label: 'nav.booking.reportsBookings',
            icon: Ticket,
            children: [
              { key: 'booking-sales', label: 'nav.booking.sales', icon: TrendingUp, to: '/booking/sales', keywords: ['events', 'attendance', 'fill rate', 'booking sales'], status: 'built' },
              { key: 'booking-payments-history', label: 'nav.booking.paymentHistory', icon: FileClock, to: '/booking/reports/purchaseshistory', keywords: ['booking payments'], status: 'built' },
            ],
          },
          // The same division for subscriptions. "Sales" here is the
          // memberships dashboard — the Overview and Plans tabs of the old
          // /memberships page, which the management split left without a home.
          {
            key: 'booking-reports-subscriptions',
            label: 'nav.booking.reportsSubscriptions',
            icon: Repeat,
            children: [
              { key: 'subscription-sales', label: 'nav.booking.sales', icon: TrendingUp, to: '/booking/subscription-sales', keywords: ['memberships', 'plans', 'subscription sales'], status: 'built' },
              { key: 'subscription-history', label: 'nav.booking.paymentHistory', icon: FileClock, to: '/booking/reports/subscriptionhistory', keywords: ['subscription payments'], status: 'built' },
            ],
          },
          { key: 'voucher-report', label: 'nav.booking.giftVouchersReport', icon: Ticket, to: '/booking/reports/vouchers', keywords: ['vouchers', 'gift certificates', 'liability', 'certificados'], flag: 'hasBookingVouchers', status: 'built' },
          { key: 'timeslot-hosts', label: 'nav.booking.timeslotHosts', icon: Users, to: '/booking/reports/timeslot/hosts', keywords: ['instructors', 'coaches', 'schedule'], status: 'built' },
          {
            key: 'booking-heatmaps',
            label: 'nav.booking.heatmaps',
            icon: Flame,
            children: [
              { key: 'heatmap-sales', label: 'nav.booking.heatmapSales', to: '/booking/heatmap/sales', keywords: ['busiest hours'], status: 'built' },
              { key: 'heatmap-bookings', label: 'nav.booking.heatmapBookings', to: '/booking/heatmap/bookings', keywords: ['busiest hours'], status: 'built' },
            ],
          },
        ],
      },
    ],
  },

  // ----------------------------------------------------------------- finance
  {
    key: 'finance',
    label: 'nav.sections.finance',
    items: [
      {
        key: 'global-payouts',
        phase: 'later',
        label: 'nav.globalPayouts',
        icon: Banknote,
        to: '/globalpayouts',
        permission: 'isFinance',
      },
      // Merged from two: Platform > Admin Services > Gift Cards (SuperAdmin,
      // search + create) and Finance > Reports > Gift Cards (Finance |
      // TopAnalytics, search + history). They called the same endpoints and
      // differed by one button, so the split was only ever a role workaround.
      //
      // It sits beside Reports rather than inside it because it is not one: it
      // reverses transactions, recalculates balances and reassigns owners. The
      // booking section was restructured for the same reason.
      {
        key: 'gift-cards',
        phase: 5,
        label: 'nav.giftCards',
        icon: Gift,
        to: '/giftcards',
        permission: 'isFinance',
        keywords: ['gift card', 'balance', 'liability', 'redeem'],
        status: 'built',
      },
      {
        key: 'finance-reports',
        phase: 'later',
        label: 'nav.reports',
        icon: BarChart3,
        permission: 'isFinance',
        children: [
          {
            key: 'finance-sales-group',
            label: 'nav.finance.sales',
            icon: TrendingUp,
            children: [
              // Was four pages — General, In Store, In-Store Employees and By
              // Station — that showed the same measures grouped four ways. The
              // grouping is a filter now; the old paths redirect here.
              { key: 'finance-sales-general', label: 'nav.finance.salesReport', to: '/finance/sales', keywords: ['by day', 'by lane', 'by station', 'employees', 'clerk', 'in store', 'tender'], phase: 5, status: 'built' },
              // Was `/finance/terminal`; the old path redirects here.
              { key: 'finance-settlements', label: 'nav.finance.settlements', to: '/finance/settlements', keywords: ['terminal', 'batch', 'close', 'deposit', 'card'], phase: 5, status: 'built' },
              // Was `/finance/pos/summary`; the old path redirects here.
              { key: 'finance-pos-summary', label: 'nav.finance.summary', to: '/finance/summary', keywords: ['z report', 'end of day', 'tender', 'tax', 'drops', 'collected'], phase: 5, status: 'built' },
            ],
          },
          // Was four sibling pages — Division, Group, Department, Category —
          // calling four pairs of endpoints that differ only in their GROUP BY.
          // The level is a filter on one page now, so the group is gone.
          {
            key: 'finance-movement',
            phase: 5,
            label: 'nav.finance.movement',
            icon: ArrowDownUp,
            to: '/finance/movement',
            keywords: [
              'division',
              'group',
              'department',
              'category',
              'modifiers',
              'sold',
            ],
            status: 'built',
          },
          {
            key: 'finance-loss',
            label: 'nav.finance.loss',
            icon: TrendingDown,
            children: [
              { key: 'loss-target-vs-actual', label: 'nav.finance.targetVsActual', to: '/reports/loss/targetvsactual' },
              { key: 'loss-participation', label: 'nav.finance.participation', to: '/reports/loss/participation' },
              { key: 'loss-monthly', label: 'nav.finance.monthly', to: '/reports/loss/sales/monthly' },
            ],
          },
          // Renamed: the original's bare "POS Discounts" was the same label the
          // Marketing section used for the page that maintains them.
          { key: 'report-pos-discounts', label: 'nav.finance.posDiscountReport', icon: Percent, to: '/finance/pos/discounts', permission: 'viewDiscountReport', keywords: ['till discounts', 'usage', 'given away'], phase: 5, status: 'built' },
          { key: 'report-inventory', label: 'nav.finance.inventoryReport', icon: Boxes, to: '/reports/inventory', keywords: ['shrink', 'loss', 'movements', 'stock value'], phase: 5, status: 'built' },
          { key: 'report-invoices', label: 'nav.finance.invoices', icon: Receipt, to: '/invoices' },
          { key: 'report-top-products', label: 'nav.finance.topProducts', icon: Sparkles, to: '/topProducts', requiresBusiness: true },
        ],
      },
    ],
  },

  // ----------------------------------------------------------------- catalog
  // The original called this group "Products". Every entry in it is catalog
  // maintenance, and "Products" already means the store product search below.
  {
    key: 'catalog',
    label: 'nav.sections.catalog',
    items: [
      {
        key: 'catalog-group',
        phase: 3,
        label: 'nav.catalog.title',
        icon: Database,
        permission: 'isStoreAdmin',
        children: [
          { key: 'item-master', label: 'nav.catalog.itemMaster', icon: Database, to: '/itemmaster', keywords: ['products', 'catalogue', 'items'], status: 'built' },
          { key: 'item-registration', label: 'nav.catalog.itemRegistration', icon: ClipboardCheck, to: '/itemregistration', keywords: ['library', 'gtin', 'assign'], status: 'built' },
          { key: 'departments', label: 'nav.catalog.departments', icon: Layers, to: '/departments', keywords: ['menu', 'supersections'], status: 'built' },
          { key: 'sections', label: 'nav.catalog.categories', icon: Grid2x2, to: '/sections', keywords: ['menu', 'sections', 'aisles'], status: 'built' },
          { key: 'search-optimization', label: 'nav.catalog.searchOptimization', icon: Search, to: '/searchoptimization', keywords: ['tags', 'synonyms', 'findability'], status: 'built' },
          { key: 'consumer-search', label: 'nav.catalog.consumerSearch', icon: Search, to: '/consumersearch', requiresBusiness: true },
          { key: 'item-prices', label: 'nav.catalog.itemPrices', icon: Tag, to: '/itemprices', flag: 'hasPriceDb', keywords: ['pos', 'cost', 'promo'], status: 'built' },
          { key: 'requirements', label: 'nav.catalog.requirements', icon: ListChecks, to: '/requirements', flag: 'managesReqSideAddons', keywords: ['modifiers', 'options'], status: 'built' },
          { key: 'sides', label: 'nav.catalog.sides', icon: Tag, to: '/sides', flag: 'managesReqSideAddons', keywords: ['modifiers', 'options'], status: 'built' },
          { key: 'addons', label: 'nav.catalog.addons', icon: Tag, to: '/addons', flag: 'managesReqSideAddons', keywords: ['modifiers', 'extras'], status: 'built' },
        ],
      },
    ],
  },

  // --------------------------------------------------------------- marketing
  {
    key: 'marketing',
    label: 'nav.sections.marketing',
    items: [
      {
        key: 'marketing-group',
        phase: 'later',
        label: 'nav.marketing.title',
        icon: Megaphone,
        permission: 'isMarketing',
        children: [
          { key: 'promotions', label: 'nav.marketing.promotions', icon: Percent, to: '/promotions' },
          { key: 'promo-codes', label: 'nav.marketing.promoCodes', icon: Ticket, to: '/promocodes', keywords: ['coupons', 'discount codes', 'vouchers'], phase: 5, status: 'built' },
          { key: 'promo-banners', label: 'nav.marketing.bannerAds', icon: Image, to: '/promobanners', keywords: ['banners', 'carousel', 'promos', 'ads'], phase: 5, status: 'built' },
          { key: 'promo-emails', label: 'nav.marketing.promoEmails', icon: Mail, to: '/promoemails' },
          { key: 'push-notifications', label: 'nav.marketing.pushNotifications', icon: MessageSquare, to: '/pushnotifications' },
          { key: 'product-campaigns', label: 'nav.marketing.productCampaign', icon: Megaphone, to: '/productcampaigns' },
          { key: 'info-cards', label: 'nav.marketing.infoCards', icon: AlertCircle, to: '/infocards', keywords: ['promos', 'panels', 'banners', 'app cards'], phase: 5, status: 'built' },
          { key: 'sponsorships', label: 'nav.marketing.sponsorships', icon: Handshake, to: '/sponsorships', keywords: ['sponsored items', 'brand deals'], phase: 5, status: 'built' },
          { key: 'marketing-pos-discounts', label: 'nav.marketing.posDiscounts', icon: Percent, to: '/posdiscounts', keywords: ['till discounts', 'cashier'], phase: 5, status: 'built' },
          { key: 'loyalty', label: 'nav.marketing.loyalty', icon: Award, to: '/loyalty', flag: 'hasLoyalty', keywords: ['points', 'rewards', 'redeem'], phase: 5, status: 'built' },
        ],
      },
    ],
  },

  // ------------------------------------------------------------------- store
  // Everything here needs a selected store; the original wrapped the whole
  // block in `{business && …}`.
  {
    key: 'store',
    label: 'nav.sections.store',
    items: [
      {
        key: 'store-group',
        phase: 'later',
        label: 'nav.store.title',
        icon: Store,
        requiresBusiness: true,
        children: [
          { key: 'store-orders', label: 'nav.store.orders', icon: ShoppingCart, to: '/order-search', permission: 'isStoreAdmin' },
          { key: 'store-customers', label: 'nav.store.customers', icon: Users, to: '/customersbystore', permission: 'isStoreManager' },
          { key: 'store-bins', label: 'nav.store.storage', icon: Inbox, to: '/bins', permission: 'isStoreAdmin' },
          { key: 'merchandiser', label: 'nav.store.onBehalf', icon: ShoppingBag, to: '/merchandiser', permission: 'isMerchandiser' },
          { key: 'products-search', label: 'nav.store.products', icon: PackageSearch, to: '/products-search', permission: 'isStoreManager' },
          { key: 'store-item-prices', label: 'nav.store.storeItemPrices', icon: Tag, to: '/storeitemprices', permission: 'isStoreManager', flag: 'hasPriceDb' },
          { key: 'current-shopper', label: 'nav.store.shopper', icon: ShoppingBag, to: '/currentshopper', permission: 'isStoreManager' },
          { key: 'store-coupons', label: 'nav.store.storeCoupons', icon: Ticket, to: '/storecoupons', permission: 'isStoreManager' },
          { key: 'self-checkout', label: 'nav.store.selfCheckout', icon: Monitor, to: '/selfcheckout', permission: 'isStoreManager' },
          { key: 'inventory', label: 'nav.store.inventory', icon: Boxes, to: '/inventory', permission: 'hasInventory', keywords: ['stock', 'quantity on hand', 'receiving', 'adjustments'], phase: 5, status: 'built' },
          { key: 'performance', label: 'nav.store.performance', icon: TrendingUp, to: '/performance', permission: 'isStoreManager' },
          { key: 'product-sorting', label: 'nav.store.sorting', icon: ArrowDownUp, to: '/sorting', permission: 'isStoreManager', tag: 'Beta' },
          { key: 'store-messaging', label: 'nav.store.chats', icon: MessageSquare, to: '/messaging-store', permission: 'isStoreManager', tag: 'Alpha', disabled: true },
          { key: 'paypal-invoices-store', label: 'nav.store.paypalInvoices', icon: Receipt, to: '/paypal/store/invoices', permission: 'isStoreManager' },
          { key: 'store-logs', label: 'nav.store.logs', icon: ScrollText, to: '/businesslogs' },
          {
            key: 'staff',
            phase: 4,
            label: 'nav.store.staff',
            icon: Users,
            permission: 'isStoreManager',
            children: [
              { key: 'staff-list', label: 'nav.store.staffList', to: '/team', keywords: ['team', 'employees', 'privileges'], status: 'built' },
              { key: 'timesheets', label: 'nav.store.timesheets', to: '/team/timesheets', keywords: ['hours', 'payroll', 'clock'], status: 'built' },
              { key: 'timesheet-types', label: 'nav.store.timesheetTypes', to: '/team/timesheet/types', keywords: ['shift', 'clock', 'hours'], status: 'built' },
            ],
          },
          {
            key: 'store-pos',
            phase: 4,
            label: 'nav.store.pos',
            icon: Monitor,
            permission: 'isStoreManager',
            children: [
              { key: 'pos-lanes', label: 'nav.store.posLanes', to: '/pos', keywords: ['till', 'register', 'lane'], status: 'built' },
              { key: 'pos-cash-drawer', label: 'nav.store.cashDrawerReport', to: '/pos/cashdrawer/report', keywords: ['till', 'cash', 'balance', 'shortage'], status: 'built' },
            ],
          },
          {
            key: 'layaway',
            label: 'nav.store.layaway',
            icon: PackageSearch,
            flag: 'hasLayaway',
            children: [
              { key: 'layaway-bins', label: 'nav.store.bins', to: '/layaway/bins' },
              { key: 'layaway-list', label: 'nav.store.layaways', to: '/layaways' },
            ],
          },
        ],
      },
      // Was the "3PL Providers" group. Its other page, Delivery Reports
      // (/deliveryproviderreports), is removed: no client uses it. One client
      // uses this one, so it takes three gates — the deployment says so
      // (`VITE_HAS_3PL_DELIVERIES`), the store has delivery switched on, and
      // the account is the store's admin or the provider's staff.
      {
        key: 'deliveries',
        label: 'nav.store.deliveries',
        icon: Truck,
        to: '/deliveries',
        requiresBusiness: true,
        feature: 'thirdPartyDelivery',
        flag: 'allowDelivery',
        permission: 'isDeliveryProvider',
        keywords: ['3pl', 'drivers', 'dispatch', 'route', 'zones', 'sorting'],
        status: 'built',
      },
      {
        key: 'store-payments',
        phase: 'later',
        label: 'nav.store.payments',
        icon: CreditCard,
        requiresBusiness: true,
        permission: 'isStoreManager',
        children: [
          { key: 'store-payouts', label: 'nav.store.payouts', icon: PiggyBank, to: '/payouts', permission: 'isFinance' },
          { key: 'payment-config', label: 'nav.store.paymentOptions', icon: SlidersHorizontal, to: '/payment-config', keywords: ['tender', 'card', 'cash', 'ath'], status: 'built' },
        ],
      },
      {
        key: 'store-settings',
        phase: 4,
        label: 'nav.store.settings',
        icon: Settings,
        requiresBusiness: true,
        permission: 'isStoreManager',
        children: [
          { key: 'business-info', label: 'nav.store.general', icon: Building2, to: '/business-info', keywords: ['address', 'pickup', 'location'], status: 'built' },
          { key: 'store-options', label: 'nav.store.options', icon: SlidersHorizontal, to: '/store-options', keywords: ['settings', 'alerts', 'fees'], status: 'built' },
          { key: 'store-hours', label: 'nav.store.hours', icon: Clock, to: '/hours' },
          { key: 'store-delivery', label: 'nav.store.delivery', icon: Truck, to: '/delivery' },
          { key: 'custom-departments', label: 'nav.store.customDepartments', icon: Layers, to: '/customdepartments' },
          { key: 'store-documents', label: 'nav.store.storeDocuments', icon: FileText, to: '/store-documents' },
        ],
      },
    ],
  },

  // ----------------------------------------------------------------- support
  // The original left these four floating at the root of the menu, below every
  // collapsed group, where they read as leftovers.
  {
    key: 'support',
    label: 'nav.sections.support',
    items: [
      // Help (/faq) and Ticket Boards (/ticketboards) removed: unused features.
      { key: 'downloads', label: 'nav.downloads', icon: Download, to: '/downloads', phase: 'later' },
      { key: 'check-health', label: 'nav.checkHealth', icon: Activity, to: '/check-health', permission: 'isSuperAdmin', phase: 'later' },
    ],
  },
];

/** Flattened leaves — powers the command palette and breadcrumb lookups. */
export interface FlatNavItem extends NavItem {
  to: string;
  sectionLabel?: string;
  /** Immediate parent group's label. */
  parentLabel?: string;
}

/**
 * Walk the tree to its leaves, carrying each ancestor's gating down. A child
 * that sets its own gate overrides the inherited one; otherwise a leaf must
 * satisfy every gate above it.
 */
function flatten(
  items: NavItem[],
  section: NavSection,
  inherited: Partial<NavItem> = {},
  parentLabel?: string
): FlatNavItem[] {
  return items.flatMap((item) => {
    const merged: NavItem = {
      ...item,
      permission: item.permission ?? inherited.permission,
      feature: item.feature ?? inherited.feature,
      flag: item.flag ?? inherited.flag,
      requiresBusiness: item.requiresBusiness ?? inherited.requiresBusiness,
      globalOnly: item.globalOnly ?? inherited.globalOnly,
      icon: item.icon ?? inherited.icon,
      phase: item.phase ?? inherited.phase,
      status: item.status ?? inherited.status,
    };

    if (merged.children?.length) {
      return flatten(merged.children, section, merged, merged.label);
    }

    // Actions are not addressable, so they never appear in search or crumbs.
    if (!merged.to) return [];

    return [{ ...merged, to: merged.to, sectionLabel: section.label, parentLabel }];
  });
}

export const flatNavItems: FlatNavItem[] = navigation.flatMap((section) =>
  flatten(section.items, section)
);
